import { useEffect, useState, useRef } from "react";
import { getCurrentUser, searchUsers, zafClient } from "@services/Zendesk";
import type { CustomObjectsFields } from "@pages/home/interface";

const customObjectFieldsCache = {
  data: null as string[] | null,
  promise: null as Promise<string[]> | null,
};

export async function loadCustomObjectFieldsOnce(): Promise<string[]> {
  if (customObjectFieldsCache.data) return customObjectFieldsCache.data;
  if (customObjectFieldsCache.promise) return customObjectFieldsCache.promise;
  
  customObjectFieldsCache.promise = (async () => {
    const response = await zafClient.request({
      url: "/api/v2/custom_objects/contas_do_ticket/fields",
      type: "GET",
    });
    
    const responseCustom = response as CustomObjectsFields;
    const fields = responseCustom.custom_object_fields.map(
      (field: { key: string }) => field.key
    );
    customObjectFieldsCache.data = fields;
    return fields;
  })();
  
  return customObjectFieldsCache.promise;
}

export function useCustomObjectFields() {
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    
    (async () => {
      try {
        let mappedFields = await loadCustomObjectFieldsOnce();

        const user = await getCurrentUser(zafClient);
        const alcada = await searchUsers(zafClient, `email:${user.email}`);
        const userAlcada = alcada[0].user_fields.alcada_do_usuario;

        if (userAlcada === "at_sac") {
          const excludedFields = ["juiz", "advogado", "procon", "tribunal", "remetente", "destinatario"];
          mappedFields = mappedFields.filter(field => !excludedFields.includes(field));

          const sacOrder = ["cliente", "posto", "transportadora", "revenda"];
          mappedFields = orderFieldsByPriority(mappedFields, sacOrder);
        }

        if (userAlcada === "at_ecom") {
          const excludedFields = ["juiz", "advogado", "procon", "revenda", "posto", "tribunal"];
          mappedFields = mappedFields.filter(field => !excludedFields.includes(field));
        }

        if (userAlcada === "at_jur") {
          const excludedFields = ["procon", "juiz", "tribunal", "remetente", "destinatario"];
          mappedFields = mappedFields.filter(field => !excludedFields.includes(field));
        }
        setAvailableRoles(mappedFields);
      } catch (error) {
        console.error("Error fetching custom object fields:", error);
      }
    })();
  }, []); 

  return { availableRoles };
}

function orderFieldsByPriority(fields: string[], priorityOrder: string[]): string[] {
  return fields.sort((a, b) => {
    const indexA = priorityOrder.indexOf(a);
    const indexB = priorityOrder.indexOf(b);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });
}